/**
 * usePlans.ts — fetch plan + pricing data for a service
 *
 * Calls GET /api/broker/v1/services/:slug/plans and returns the plan list
 * in a simple async state machine (idle | loading | loaded | error).
 *
 * Token: obtained per-fetch via getToken() (refreshes first when needed) —
 * fix 2026-08-02: the modal used to send the login-time context token, which
 * expired after accessTokenLifespan and produced 401s in long sessions.
 *
 * Pricing structure returned by the API:
 *   plan.pricing = { [currency]: { price_cents, billing_period, trial_days, feature_limit } }
 *
 * Trial detection per SL-R-009 + decision-2026-05-30-subscription-as-registration:
 *   trial = price_cents === 0 && trial_days > 0 (never a "free" flag)
 *
 * WP-SL-V3 / DOPLA-686
 */

import { useEffect, useReducer } from 'react';

export interface PricingEntry {
  price_cents: number;
  billing_period: string;
  trial_days: number;
  feature_limit?: Record<string, unknown> | null;
  // SL-R-009 metric fields — populated by WP-SL-M5 when available
  metric?: string;
  unit_size?: number;
  unit_price?: number;
}

export interface Plan {
  id: string;
  name: string;
  description: string | null;
  is_default: boolean;
  status: string;
  /** pricing keyed by currency (e.g. "CHF", "EUR") */
  pricing: Record<string, PricingEntry>;
}

export type PlansState =
  | { tag: 'idle' }
  | { tag: 'loading' }
  | { tag: 'loaded'; plans: Plan[] }
  | { tag: 'error'; errorCode?: string };

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; plans: Plan[] }
  | { type: 'FETCH_ERROR'; errorCode?: string };

function reducer(_: PlansState, action: Action): PlansState {
  switch (action.type) {
    case 'FETCH_START':   return { tag: 'loading' };
    case 'FETCH_SUCCESS': return { tag: 'loaded', plans: action.plans };
    case 'FETCH_ERROR':   return { tag: 'error', errorCode: action.errorCode };
    default:              return _;
  }
}

export function usePlans(
  serviceSlug: string | null,
  getToken: () => Promise<string | null>,
) {
  const [state, dispatch] = useReducer(reducer, { tag: 'idle' });

  useEffect(() => {
    if (!serviceSlug) return;
    let cancelled = false;
    dispatch({ type: 'FETCH_START' });
    (async () => {
      try {
        const token = await getToken();
        if (cancelled) return;
        if (!token) {
          dispatch({ type: 'FETCH_ERROR', errorCode: '401' }); return;
        }
        const res = await fetch(`/api/broker/v1/services/${encodeURIComponent(serviceSlug)}/plans`, {
          cache: 'no-store',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!res.ok) {
          dispatch({ type: 'FETCH_ERROR', errorCode: String(res.status) }); return;
        }
        const { plans } = await res.json() as { plans: Plan[] };
        if (!cancelled) dispatch({ type: 'FETCH_SUCCESS', plans });
      } catch {
        if (!cancelled) dispatch({ type: 'FETCH_ERROR', errorCode: 'NETWORK_ERROR' });
      }
    })();
    return () => { cancelled = true; };
  }, [serviceSlug, getToken]);

  return state;
}
